/* ============================================================
   screen-mypage.jsx — My works / profile
   ============================================================ */

import api from "./api/api.js";
import { normalizeImageItems } from "./api/image-view-model.js";
import CONFIG from "./config/config.js";
import { useQuery } from "@tanstack/react-query";

function MyPage({ auth, onLogin, go, open, liked, onLike, layout, onItemsLoaded, initialTab = "created" }) {
  const { lang, t } = useT();
  const [tab, setTab] = React.useState(initialTab);

  React.useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const profileQuery = useQuery({
    queryKey: ["users", "me"],
    queryFn: () => api.users.me(),
    enabled: Boolean(auth),
    staleTime: 60_000,
  });
  const creditsQuery = useQuery({
    queryKey: ["users", "credits"],
    queryFn: () => api.users.credits(),
    enabled: Boolean(auth),
    staleTime: 30_000,
  });
  const worksQuery = useQuery({
    queryKey: ["users", "works", auth?.id || auth?.handle || "me", tab],
    queryFn: async () => normalizeImageItems((await api.users.works({ type: tab }))?.items || []),
    enabled: Boolean(auth),
    staleTime: 30_000,
  });

  const tabs = [
    { id: "created", label: t("tab_created") },
    CONFIG.FEATURES.social ? { id: "saved", label: t("tab_saved") } : null,
    CONFIG.FEATURES.social ? { id: "liked", label: t("tab_liked") } : null,
  ].filter(Boolean);

  const profilePayload = profileQuery.data;
  const profileSource = profilePayload?.user || profilePayload?.profile || profilePayload || null;
  const profile = profileSource ? normalizeProfileUser(profileSource, auth) : auth;
  const stats = profilePayload?.stats || profileSource?.stats || null;
  const credits = creditsQuery.data || profilePayload?.credits || null;
  const displayUser = normalizeProfileUser(profile || auth, auth);
  const current = React.useMemo(() => {
    if (worksQuery.data) {
      return worksQuery.data.map((item) => (
        tab === "created" ? { ...item, author: displayUser } : item
      ));
    }
    if (worksQuery.isError) return fallbackWorks(tab, auth, liked);
    return [];
  }, [worksQuery.data, worksQuery.isError, tab, displayUser, auth, liked]);
  const worksError = worksQuery.isError && !CONFIG.USE_MOCK
    ? (lang === "en" ? "Could not load your works from the backend." : "백엔드에서 내 작업을 불러오지 못했어요.")
    : "";

  React.useEffect(() => {
    if (worksQuery.data) onItemsLoaded?.(current);
  }, [worksQuery.data, current, onItemsLoaded]);
  const statsModel = {
    works: numberOr(stats?.works, stats?.created, current.length),
    likesReceived: numberOr(stats?.likesReceived, stats?.likes, 0),
    followers: numberOr(stats?.followers, 0),
    following: numberOr(stats?.following, 0),
  };
  const creditBalance = numberOr(credits?.balance, credits?.remaining, 86);

  if (!auth) {
    return <LoginGate onLogin={onLogin} go={go} title={t("mp_login_title")} desc={t("mp_login_desc")} primaryLabel={t("login")} icon="user" primaryIcon={null} features={false} />;
  }

  return (
    <div>
      {/* profile banner */}
      <div style={{ background: "var(--paper)", borderBottom: "1px solid var(--line)" }}>
        <div className="container" style={{ padding: "28px 32px 22px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <div style={{ borderRadius: "50%", flex: "none" }}>
              <Avatar author={displayUser} size={92} />
            </div>
            <div style={{ flex: 1, minWidth: 200, paddingBottom: 4 }}>
              <h1 style={{ fontSize: 26 }}>{displayUserName(displayUser)}</h1>
              <p className="muted" style={{ fontSize: 14 }}>@{displayUser.handle} · {t("role_creator")}</p>
            </div>
            <div style={{ display: "flex", gap: 8, paddingBottom: 4 }}>
              {CONFIG.FEATURES.generation && (
                <button className="btn btn-spark" onClick={() => go("generate")}><Icon name="wand" size={17} stroke={2} /> {t("mp_new")}</button>
              )}
              <button className="btn btn-line btn-icon" aria-label={t("settings")} onClick={() => go("settings")}><Icon name="settings" size={19} /></button>
            </div>
          </div>

          {/* stats + credits */}
          <div style={{ display: "flex", gap: 26, marginTop: 20, flexWrap: "wrap", alignItems: "center" }}>
            {[[t("st_works"), statsModel.works], [t("st_likes_recv"), statsModel.likesReceived], [t("st_followers"), statsModel.followers], [t("st_following"), statsModel.following]].map(([l, v]) => (
              <div key={l} style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
                <span className="tnum" style={{ fontSize: 19, fontWeight: 800 }}>{formatCount(v)}</span>
                <span className="muted" style={{ fontSize: 13.5 }}>{l}</span>
              </div>
            ))}
            <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10,
              background: "var(--paper)", border: "1px solid var(--line)", borderRadius: "var(--r-pill)",
              padding: "8px 16px", flex: "none" }}>
              <Icon name="zap" size={17} stroke={2} style={{ color: "var(--ink)", flex: "none" }} />
              <span className="tnum" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap" }}>{t("credit_badge", { n: creditBalance })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* tabs */}
      <div style={{ position: "sticky", top: 64, zIndex: 30, background: "color-mix(in srgb, var(--k-bg) 86%, transparent)",
        backdropFilter: "saturate(180%) blur(12px)", borderBottom: "1px solid var(--line)" }}>
        <div className="container" style={{ padding: "0 32px", display: "flex", gap: 4 }}>
          {tabs.map((tb) => (
            <button key={tb.id} onClick={() => go({ name: "mypage", tab: tb.id })} style={{
              height: 50, padding: "0 14px", fontSize: 15, fontWeight: 700, whiteSpace: "nowrap",
              color: tab === tb.id ? "var(--ink)" : "var(--ink-3)", position: "relative",
            }}>
              {tb.label}
              <span style={{ position: "absolute", left: 8, right: 8, bottom: 0, height: 3, borderRadius: 3,
                background: tab === tb.id ? "var(--k-text)" : "transparent" }} />
            </button>
          ))}
        </div>
      </div>

      {/* grid */}
      <div className="container" style={{ padding: "24px 32px 72px", minHeight: "40vh" }}>
        {worksQuery.isLoading ? (
          <div className="uniform-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="shimmer" style={{ aspectRatio: "1 / 1", borderRadius: "var(--r-md)" }} />
            ))}
          </div>
        ) : worksError ? (
          <EmptyState
            title={lang === "en" ? "Works unavailable" : "작업을 불러올 수 없어요"}
            desc={worksError}
            action={<button className="btn btn-spark" style={{ marginTop: 18 }} onClick={() => worksQuery.refetch()}>
              <Icon name="refresh" size={16} /> {lang === "en" ? "Retry" : "다시 시도"}
            </button>}
          />
        ) : current.length === 0 ? (
          <EmptyState
            title={tab === "liked" ? t("mp_empty_liked_title") : t("mp_empty_title")}
            desc={tab === "liked" ? t("mp_empty_liked_desc") : t("mp_empty_desc")}
            action={<button className="btn btn-spark" style={{ marginTop: 18 }} onClick={() => go(tab === "liked" ? "gallery" : "generate")}>
              {tab === "liked" ? t("mp_view_gallery") : t("mp_make_image")}
            </button>}
          />
        ) : (
          <div className="uniform-grid">
            {current.map((it, i) => (
              <ImageCard key={it.id + tab} item={it} index={i} layout="uniform" liked={liked.has(it.id) || it.liked} onLike={onLike} onOpen={open} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function normalizeProfileUser(user, fallback) {
  const base = fallback || { name: "김디자인", nameEn: "Designer Kim", handle: "designer" };
  if (!user) return base;
  const name = user.displayName || user.name || user.nickname || user.handle || base.name;
  return {
    ...base,
    ...user,
    name,
    nameEn: name,
    handle: user.handle || user.username || base.handle || "designer",
  };
}

function fallbackWorks(tab, auth, liked) {
  if (!CONFIG.USE_MOCK) return [];
  if (tab === "created") return GALLERY.slice(0, 8).map((g, index) => ({ ...g, author: auth, createdDays: index + 1 }));
  if (tab === "liked") return GALLERY.filter((g) => liked.has(g.id));
  return GALLERY.slice(10, 16);
}

function numberOr(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return 0;
}

function formatCount(value) {
  return typeof value === "number" ? value.toLocaleString() : value;
}

Object.assign(window, { MyPage });
